import { useEffect, useRef, useState } from "react";

/**
 * Tween a number towards its new value, odometer-style.
 *
 * WHY: accepting a suggestion moves the score, and an integer that simply swaps from 33 to 41 is easy
 * to miss entirely — the original complaint about this panel was that clicking suggestions appeared to
 * do nothing. Counting through the intermediate values makes the change the thing the eye follows, and
 * ties it to the click that caused it. The donut's arc already animates over the same period, so the
 * number and the ring move together.
 *
 * Interruptible on purpose: a student clicking Accept three times quickly must not queue three
 * animations. Each new target tweens from wherever the display currently is, so rapid clicks read as
 * one continuous climb rather than a stutter.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */

/** Long enough to read as counting, short enough not to delay the feedback. Matches the arc's 500ms. */
const DEFAULT_DURATION_MS = 550;

/** Ease-out cubic: fast enough to feel responsive, settling rather than stopping dead. */
const easeOut = (t: number) => 1 - (1 - t) ** 3;

// eslint-disable-next-line lingui/no-unlocalized-strings -- a CSS media query, not display text
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const prefersReducedMotion = () => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
};

export const useAnimatedNumber = (value: number, durationMs = DEFAULT_DURATION_MS) => {
  const [display, setDisplay] = useState(value);

  // The animation's starting point, kept in a ref so an interrupting change tweens from the CURRENT
  // on-screen value rather than restarting from the previous target.
  const currentRef = useRef(value);
  const frameRef = useRef<number>();

  useEffect(() => {
    const from = currentRef.current;
    const to = value;

    if (from === to) return;

    // Accessibility, and correctness for anyone who has asked the OS to stop animating things: land on
    // the value immediately rather than tweening.
    if (prefersReducedMotion()) {
      currentRef.current = to;
      setDisplay(to);
      return;
    }

    const startedAt = performance.now();

    const step = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / durationMs);
      const next = from + (to - from) * easeOut(progress);

      currentRef.current = next;
      setDisplay(next);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(step);
        return;
      }

      // Snap to the exact target. Floating-point easing lands a hair short, and the displayed integer
      // must match the authoritative number, not a rounding of something near it.
      currentRef.current = to;
      setDisplay(to);
    };

    frameRef.current = requestAnimationFrame(step);

    return () => {
      if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current);
    };
  }, [value, durationMs]);

  return display;
};
